import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Header, Icon, Label, Menu, Message, Segment } from 'semantic-ui-react';
import { detectMode, getDataRefresh, getReport, refreshData, uploadSourceData, keyOfSummary, listConfig, listReports, resetDemo, toModel } from './api.js';
import { parseSourceFile } from './sourceData.js';
import { fileBase, makeExcel, makePdf, makeSummaryExcel, saveFile } from './exportFiles.js';
import { addApproval, removeApproval } from './approvals.js';
import { defaultSelection } from './periods.js';
import { periodPickerEnabled, showHistoric } from './constants.js';
import { loadDb, resetDb, saveDb, storageLabel } from './store/dbStore.js';
import PeriodPicker from './components/PeriodPicker.jsx';
import ReportBody from './components/ReportBody.jsx';
import SummaryTab from './components/SummaryTab.jsx';
import ValidationTab from './components/ValidationTab.jsx';
import HomeTab from './components/HomeTab.jsx';
import HistoricTab from './components/HistoricTab.jsx';
import AdminTab from './components/AdminTab.jsx';
import { LOGO_URL } from './logo.js';
import { useAuth } from './auth/AuthContext.js';

const XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const EMPTY_DB = { app_user: [], report_approval: [] };
const slug = (t) => t.replace(/[^A-Za-z0-9]+/g, '_');
const USER_KEY = 'srs_demo_user';
const savedUser = () => { try { return window.localStorage.getItem(USER_KEY); } catch { return null; } };

export default function App() {
  const [reports, setReports] = useState([]);
  const [config, setConfig] = useState([]);              // settlement_report_config: every expected report
  const [refreshedAt, setRefreshedAt] = useState(null);   // when the data was last refreshed (latest_data_refresh_date), or null
  const [model, setModel] = useState(null);          // the report on screen (read-only): { report, figures, ... }
  const [sel, setSel] = useState(null);              // the period being viewed: { mode, year, quarter, month }
  const [opened, setOpened] = useState(0);           // counts report openings, so each opening starts on the Preview tab
  const [view, setView] = useState('home');          // 'home' | 'reports' | 'summary' | 'historic' | 'admin' | 'validation'
  const [status, setStatus] = useState(null);        // {text, kind}
  const [busy, setBusy] = useState(null);            // 'load' | 'finance' | 'account_manager' | 'xlsx' | 'pdf' | 'summary' | 'refresh' | 'upload'
  const [mode, setMode] = useState(null);            // null while detecting, then 'live' or 'demo' (no backend)
  const [db, setDb] = useState(EMPTY_DB);             // JSON "database": app_user + report_approval tables
  const [demoUserId, setDemoUserId] = useState(savedUser);
  const demo = mode === 'demo';
  const auth = useAuth();                              // the signed-in Microsoft user (null when sign-in is switched off)
  const timer = useRef(null);

  const say = useCallback((text, kind = 'info') => {
    setStatus({ text, kind });
    clearTimeout(timer.current);
    if (kind !== 'err') timer.current = setTimeout(() => setStatus(null), 5000);
  }, []);

  const refreshList = useCallback(async () => {
    try { setReports(await listReports()); } catch (e) { say(`Could not load the report list: ${e.message}`, 'err'); }
  }, [say]);
  useEffect(() => {                                    // use the backend if it answers, otherwise the built-in demo data
    let active = true;
    (async () => {
      const m = await detectMode();
      if (!active) return;
      setMode(m);
      refreshList();
      try { const c = await listConfig(); if (active) setConfig(Array.isArray(c) ? c : []); } catch (e) { say(`Could not load the list of expected reports: ${e.message}`, 'err'); }
      try { const r = await getDataRefresh(); if (active) setRefreshedAt(r); } catch { /* nothing to show */ }
      try { const d = await loadDb(); if (active) setDb(d); } catch (e) { say(`Could not load approvals: ${e.message}`, 'err'); }
    })();
    return () => { active = false; };
  }, [refreshList, say]);

  /* ----------------------------------------------------------------- who is acting */
  const user = useMemo(() => {
    if (auth) {                                        // real sign-in: the role comes from the Entra assignment (SAML) ...
      if (auth.role) return { user_id: auth.userId, display_name: auth.name, email: auth.username, role: auth.role, account_manager: auth.accountManager, manager_status: auth.managerStatus, is_admin: !!auth.isAdmin };
      const who = String(auth.username || '').toLowerCase();                       // ... or, for Entra OIDC sign-in, from app_user by e-mail
      return db.app_user.find((u) => u.email.toLowerCase() === who) || null;
    }
    return db.app_user.find((u) => u.user_id === demoUserId) || db.app_user[0] || null;
  }, [auth, db.app_user, demoUserId]);
  const pickUser = (id) => { setDemoUserId(id); try { window.localStorage.setItem(USER_KEY, id); } catch { /* storage blocked */ } };

  /* ---------------------------------------------------------------- derived */
  const report = model?.report;
  const years = useMemo(() => (report ? [...new Set(reports.filter((r) => r.settlement_report_id === report.settlement_report_id).map((r) => r.report_year))].sort((a, b) => b - a) : []), [reports, report]);

  /* ---------------------------------------------------------------- loading */
  const loadFull = useCallback(async (summary) => toModel(await getReport(keyOfSummary(summary))), []);
  const latestIn = useCallback((id, year) => reports.filter((r) => r.settlement_report_id === id && r.report_year === year)
    .sort((a, b) => b.as_at_date.localeCompare(a.as_at_date))[0], [reports]);
  async function open(summary, nextSel) {              // `nextSel` is the period to show; without it the report's own latest period
    setBusy('load');
    try {
      let target = summary, picked = nextSel;
      if (picked && picked.year !== summary.report_year) {   // the period is in another year: use that year's version of the report
        const sibling = latestIn(summary.settlement_report_id, picked.year);
        if (sibling) target = sibling; else picked = undefined;
      }
      const m = { ...(await loadFull(target)), key: keyOfSummary(target) };
      setModel(m); setSel(picked || defaultSelection(m.report)); setOpened((n) => n + 1); setView('reports'); setStatus(null);
    } catch (e) { say(`Could not open the report: ${e.message}`, 'err'); } finally { setBusy(null); }
  }
  function changePeriod(next) {
    if (next.year === report.report_year) { setSel(next); return; }
    const sibling = latestIn(report.settlement_report_id, next.year);
    if (sibling) open(sibling, next);
  }

  /* ---------------------------------------------------------------- approvals */
  async function approve(level, forReport, period) {   // the report and period on screen (Reports: the period due; Historic: a whole year)
    const label = level === 'finance' ? 'Finance' : 'Account Manager';
    if (!window.confirm(`Approve ${forReport.settlement_report_id} – ${period.title} as ${label}, ${user.display_name}?\nThe approval is recorded with your name and the date and time, and cannot be undone here.`)) return;
    setBusy(level);
    try {
      const fresh = await loadDb();                    // re-read first, so an approval made elsewhere in the meantime is respected
      addApproval(fresh, { level, report: forReport, period, user });
      await saveDb(fresh);
      setDb(fresh);
      say(`${label} approval recorded for ${period.title}.`, 'ok');
    } catch (e) { say(`Could not record the approval: ${e.message}`, 'err'); } finally { setBusy(null); }
  }

  async function unapprove({ level, report: forReport, period, reason }) {   // Admin: take an approval away (and keep a record of who, when and why)
    const label = level === 'finance' ? 'Finance' : 'Account Manager';
    if (!window.confirm(`Remove the ${label} approval for ${forReport.settlement_report_id} – ${period.title}?\nThe report goes back to needing approval. The removal is recorded with your name, the date and time and your reason.`)) return false;
    setBusy('unapprove');
    try {
      const fresh = await loadDb();                    // re-read first, so a change made elsewhere in the meantime is respected
      removeApproval(fresh, { level, report: forReport, period, user, reason });
      await saveDb(fresh);
      setDb(fresh);
      say(`${label} approval removed for ${period.title}.`, 'ok');
      return true;
    } catch (e) { say(`Could not remove the approval: ${e.message}`, 'err'); return false; } finally { setBusy(null); }
  }

  /* ------------------------------------------------------------ export */
  async function download(kind, { report: forReport, period, layout, status: approval }) {
    if (approval.stage !== 'complete') return;         // belt and braces: the buttons are not shown before this
    setBusy(kind);
    try {
      const name = `${fileBase(forReport)}_${slug(period.title)}`;
      if (kind === 'xlsx') saveFile(`${name}.xlsx`, await makeExcel(layout), XLSX);
      else saveFile(`${name}.pdf`, await makePdf(layout), 'application/pdf');
      say(`Built ${name}.${kind} for ${period.title}.`, 'ok');
    } catch (e) { say(`Could not build the file: ${e.message}`, 'err'); } finally { setBusy(null); }
  }
  async function downloadSummary(summary) {
    setBusy('summary');
    try {
      const name = `Summary_${summary.year}.xlsx`;
      saveFile(name, await makeSummaryExcel(summary), XLSX);
      say(`Built ${name}.`, 'ok');
    } catch (e) { say(`Could not build the file: ${e.message}`, 'err'); } finally { setBusy(null); }
  }

  async function refreshAll() {                      // Admin: run refresh_data in the database, then read everything again
    if (!window.confirm('Run refresh_data in the database now?\nThe report list, the expected reports and the validation data are then loaded again.')) return;
    setBusy('refresh');
    try {
      const done = await refreshData();
      const [list, cfg, at] = await Promise.all([listReports(), listConfig(), getDataRefresh().catch(() => null)]);
      setReports(list); setConfig(Array.isArray(cfg) ? cfg : []); setRefreshedAt(at || (done && done.latest_data_refresh_date) || null);
      setModel(null); setSel(null);                    // an open report may have changed: reopen it from Home
      say(demo ? 'Demo mode: there is no database to refresh.' : done && done.recorded === false ? 'Data refreshed, but the refresh date could not be recorded.' : 'Data refreshed.', demo ? 'info' : 'ok');
    } catch (e) { say(`Could not refresh the data: ${e.message}`, 'err'); } finally { setBusy(null); }
  }

  async function uploadFile(file) {                  // Admin: load a CSV / Excel file into source_data_current_q3
    if (!file) return false;
    setBusy('upload');
    try {
      const data = await parseSourceFile(file);
      if (!window.confirm(`Load ${file.name} into source_data_current_q3?\n${data.rows.length} row${data.rows.length === 1 ? '' : 's'}, ${data.columns.length} columns. The rows already in the table are replaced.`)) return false;
      const done = await uploadSourceData(data);
      say(demo ? `Demo mode: ${done.rows} rows read from ${file.name}; there is no database to load them into.` : `Loaded ${done.rows} rows from ${file.name} into ${done.table}.`, demo ? 'info' : 'ok');
      return true;
    } catch (e) { say(`Could not load the file: ${e.message}`, 'err'); return false; } finally { setBusy(null); }
  }

  async function resetDemoData() {
    if (!window.confirm('Reset the demo reports and all recorded approvals to the original sample?')) return;
    resetDemo(); setModel(null); setSel(null); setStatus(null);
    try { await resetDb(); setDb(await loadDb()); } catch (e) { say(`Could not reset the approvals: ${e.message}`, 'err'); return; }
    refreshList();
    say('Demo data and approvals reset to the original sample.', 'ok');
  }

  const admin = !!user && !!user.is_admin;               // the Admin menu is for the people in the admin_email table
  const navItem = (id, text) => (
    <Menu.Item as="button" type="button" className="navbtn" active={view === id} aria-current={view === id ? 'page' : undefined} onClick={() => setView(id)}>{text}</Menu.Item>
  );
  return (
    <>
      <a className="skip" href="#main">Skip to the content</a>
      <Menu as="header" inverted borderless className="topbar">
        <Menu.Item as="h1" header><Icon name="chart bar outline" />Settlement Report Studio{demo && <Label color="orange" size="tiny" className="demolabel">DEMO</Label>}</Menu.Item>
        <Menu.Menu role="group" aria-label="Sections">
          {navItem('home', 'Home')}
          {navItem('reports', 'Reports')}
          {navItem('summary', 'Summary')}
          {showHistoric() && navItem('historic', 'Historic')}
          {admin && navItem('admin', 'Admin')}
          {navItem('validation', 'Validation')}
        </Menu.Menu>
        <Menu.Menu position="right" role="group" aria-label="Account">
          {auth ? (
            <>
              <Menu.Item className="user"><Icon name="user circle" />{auth.name}{user ? ` (${user.role === 'finance' ? 'Finance' : 'Account Manager'})` : ''}</Menu.Item>
              <Menu.Item><Button type="button" basic inverted compact size="small" content="Sign out" onClick={auth.signOut} /></Menu.Item>
            </>
          ) : (
            <Menu.Item className="user">
              <Icon name="user circle" />
              <label htmlFor="acting-as" className="actinglabel">Acting as</label>
              <select id="acting-as" className="ui dropdown" value={user?.user_id || ''} onChange={(e) => pickUser(e.target.value)}>
                {db.app_user.map((u) => <option key={u.user_id} value={u.user_id}>{u.display_name} – {u.role === 'finance' ? 'Finance' : 'Account Manager'}</option>)}
              </select>
            </Menu.Item>
          )}
        </Menu.Menu>
        <Menu.Item className="logoitem"><img src={LOGO_URL} alt="Company logo" className="logo" onError={(e) => { e.currentTarget.style.display = 'none'; }} /></Menu.Item>
      </Menu>

      {demo && (
        <Message info className="demobar" role="note">
          <strong>Demo mode</strong> – no backend is connected. These are dummy reports modelled on the Excel template; they are read-only here.
          Approvals are saved in {storageLabel()}. Use “Acting as” (top right) to approve as Finance or as an Account Manager.{' '}
          <Button type="button" basic compact size="mini" onClick={resetDemoData}>Reset demo data</Button>
        </Message>
      )}

      <div className="page">
        <main id="main" aria-busy={busy === 'load'}>
          {view === 'home' ? (
            <HomeTab refreshedAt={refreshedAt} reports={reports} config={config} approvals={db.report_approval} user={user} onOpen={open} />
          ) : view === 'summary' ? (
            <SummaryTab reports={reports} config={config} approvals={db.report_approval} loadFull={loadFull} onExcel={downloadSummary} busy={busy} />
          ) : view === 'historic' && showHistoric() ? (
            <HistoricTab reports={reports} approvals={db.report_approval} user={user} busy={busy} storage={storageLabel()} loadFull={loadFull}
              onApprove={approve} onDownload={download} />
          ) : view === 'admin' ? (
            <AdminTab reports={reports} approvals={db.report_approval} user={user} busy={busy} onUnapprove={unapprove} onRefresh={refreshAll} onUpload={uploadFile} />
          ) : view === 'validation' ? (
            <ValidationTab reports={reports} config={config} approvals={db.report_approval} loadFull={loadFull} />
          ) : !model ? (
            <Segment placeholder>
              <Header icon><Icon name="file alternate outline" />Open a report from the Home page to see it here.</Header>
              <Button type="button" primary onClick={() => setView('home')}>Go to Home</Button>
            </Segment>
          ) : (
            <ReportBody key={opened} onBack={() => setView('home')} model={model} sel={sel} approvals={db.report_approval} user={user} busy={busy}
              storage={storageLabel()} onApprove={approve} onDownload={download}
              picker={<PeriodPicker sel={sel} years={years} onChange={changePeriod} disabled={!!busy || !periodPickerEnabled()} />} />
          )}
        </main>
      </div>

      {status && (status.kind === 'err'
        ? (
          <Message negative role="alert" className="toast">
            {status.text} <Button type="button" basic compact size="mini" onClick={() => setStatus(null)}>Dismiss</Button>
          </Message>
        ) : <Message as="output" aria-live="polite" success={status.kind === 'ok'} info={status.kind === 'info'} className="toast">{status.text}</Message>)}
    </>
  );
}
